import React, { useState } from "react";
import { Button, Stack } from "react-bootstrap";
import SearchBar from "../../components/search";
import { ServiceManager } from "../../services/ServiceManager";
import { APIStatusCode } from "../../app/enums";
import IAccount from "../../models/Account";
import CustomModal from "../../components/modal";
import IconElement from "../../components/icon";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import SimpleTable from "../../components/simpletable";
import { SOMETHING_WENT_WRONG } from "../../app/constants";
import MISButton from "../../components/button";
import { useNavigate } from "react-router-dom";
import MISSpinner from "../../components/spinner";

interface IEstablishmentCreate {
    createEstablishment?: any
}

export default function CreateEstablishment(props: IEstablishmentCreate) {
    const handleCloseSaveModal = () => setShowSaveModal(false);
    const [isSpinner, setSpinner] = useState<boolean>();
    const [searchValue, setSearchValue] = useState<string>();
    const [establishments, setEstablishments] = useState<IAccount[]>();
    const [showSaveModal, setShowSaveModal] = useState(false);
    let navigate = useNavigate();

    const columnHelper = createColumnHelper<IAccount>();
    const columns = [
        columnHelper.accessor('salesForceExternalID', {
            header: () => <span>External ID</span>,
            size: 25
        }),
        columnHelper.accessor('name', {
            header: () => <span>Name</span>,
            size: 25
        }),
        columnHelper.accessor('city', {
            header: () => <span>City</span>,
            size: 25
        }),
        columnHelper.accessor('postCode', {
            header: () => <span>PostCode</span>,
            size: 25
        })
    ];

    async function getEstablishmentInternal() {
        const establishmentData = await ServiceManager.EstablishmentService.GetByAccountId(searchValue, false);
        if ((establishmentData.response && establishmentData.response?.status === APIStatusCode.NotFound) 
            || (establishmentData && !establishmentData.enabled)) {
            return true;
        }
        else if(establishmentData && establishmentData.establishmentID && establishmentData.enabled) {
            ServiceManager.ToastService.showError("An establishment already exists for the account specified");
            return false;
        }
        else {
            let error = ""
            if (establishmentData.response) {
                error = establishmentData.response.data;
            }
            else {
                error = establishmentData.message;
            }
            ServiceManager.ToastService.showError(error);
            return false;
        }
    }

    async function getEstablishmentAuth() {
        const establishmentData = await ServiceManager.AuthService.GetByExternalId(searchValue!, true);
        if (establishmentData && establishmentData.salesForceExternalID) {
            let establishments: IAccount[] = [];
            establishments.push(establishmentData);
            setEstablishments(establishments);
            return true;
        }
        else {
            let error = ""
            if (establishmentData.response) {
                error = establishmentData.response.data;
            }
            else {
                error = establishmentData.message;
            }
            ServiceManager.ToastService.showError(error);
            setEstablishments(undefined);
            return false;
        }
    }

    async function fetchEstablishmentData() {
        setSpinner(true);
        if (searchValue) {
            const internalEstablishmentRsp = await getEstablishmentInternal();
            if (internalEstablishmentRsp) {
                const authEstablishmentRsp = await getEstablishmentAuth();
                if (!authEstablishmentRsp) {
                    ServiceManager.ToastService.showError("No establishment found with this External ID. Please verify the ID and try again.");
                }
            }
        }
        else {
            ServiceManager.ToastService.showError("Enter Salesforce External ID");
        }
        setSpinner(false);
    }

    function clickCancel() {
        setEstablishments(undefined);
    }

    async function saveEstablishments() {
        setSpinner(true);
        let response = await ServiceManager.EstablishmentService.Synchronise(establishments?.at(0)?.salesForceExternalID);
        
        // Success scenarios: OK (200) or Created (201)
        if (response && (response.status === APIStatusCode.Ok || response.status === APIStatusCode.Created)) {
            const successMessage = response.status === APIStatusCode.Created 
                ? "Successfully created new establishment" 
                : "Successfully updated establishment";
            ServiceManager.ToastService.showSuccess(successMessage);
            
            const establishmentID = response?.data?.establishmentID || response?.data?.EstablishmentID;
            
            if (establishmentID) {
                props.createEstablishment(false);
                setShowSaveModal(false);
                setTimeout(() => {                    
                    navigate(`/establishment/${establishmentID}`);
                }, 100);
            } else {
                ServiceManager.ToastService.showError("Establishment created but ID not found in response");
                setSpinner(false);
            }
        }
        // Error scenarios
        else {
            if (response.response) {
                const status = response.response.status;
                
                // NotFound (404) - Establishment not found in Salesforce
                if (status === APIStatusCode.NotFound) {
                    const errorMessage = response.response.data || "No establishment exists with the account ID specified in Salesforce";
                    ServiceManager.ToastService.showError(errorMessage);
                }
                // Problem Details (5xx or other errors with detail field)
                else if (response.response.data?.detail) {
                    ServiceManager.ToastService.showError(response.response.data.detail);
                }
                // Validation errors
                else if (response.response.data?.errors) {
                    let errors = Object.values(response.response.data.errors);
                    ServiceManager.ToastService.showError(`${errors.join('\n')}`);
                }
                // Title-based errors
                else if (response.response.data?.title) {
                    ServiceManager.ToastService.showError(response.response.data.title);
                }
                // Generic error message
                else if (response.response.data) {
                    ServiceManager.ToastService.showError(typeof response.response.data === 'string' 
                        ? response.response.data 
                        : SOMETHING_WENT_WRONG);
                }
                else {
                    ServiceManager.ToastService.showError(SOMETHING_WENT_WRONG);
                }
            }
            // Network or other errors without response object
            else if (response.message) {
                ServiceManager.ToastService.showError(response.message);
            }
            else {
                ServiceManager.ToastService.showError(SOMETHING_WENT_WRONG);
            }
        }
        handleCloseSaveModal();
        setSpinner(false);
    }

    const SaveConfirmationForm = () => {
        return (
            <Stack>
                <h5 className="text-center">Create account</h5>
                <p className="text-center">Do you want to create this account and all child account records.</p>
                <Stack direction="horizontal" className="mt-3">
                    {
                        (isSpinner === true)
                            ?
                            <Stack>
                                <MISSpinner/>
                            </Stack>
                            :
                            <React.Fragment>
                                <Button variant="popup-btn right-margin10 btn-outline-secondary w-50" onClick={handleCloseSaveModal}>
                                    Cancel
                                </Button>
                                <Button variant="popup-btn btn-primary crbc-bg-color w-50" onClick={saveEstablishments}>
                                    Confirm
                                </Button>
                            </React.Fragment>
                    }
                </Stack>
            </Stack>
        )
    }

    return (
        <React.Fragment>
            <label className="mt-3 label-text">Enter Salesforce External ID </label>
            <Stack direction="horizontal">
                <SearchBar placeholder="Acc-xxxx" className="w-25" onChange={(searchText: string) => setSearchValue(searchText)} />
                <MISButton disabled={!searchValue} text="Search" className="popup-btn btn btn-primary crbc-bg-color" FnOnClick={() => fetchEstablishmentData()} />
            </Stack>
            {
                (isSpinner === true)
                    ?
                    <Stack className="mt-5">
                        <MISSpinner/>
                    </Stack>
                    : null
            }

            {
                (establishments !== undefined)
                    ?
                    <div id="createEstablishment" className="mt-3 ms-2">
                        <SimpleTable
                            tableKey={"establishments"}
                            columns={columns}
                            data={establishments}
                        />
                        <Stack direction="horizontal" className="mt-5">
                            <MISButton disabled={isSpinner} text="Cancel" className="popup-btn btn btn-outline-secondary me-2" FnOnClick={() => clickCancel()} />
                            <MISButton disabled={isSpinner} text="Create" className="popup-btn btn btn-primary crbc-bg-color" FnOnClick={() => setShowSaveModal(true)} />
                        </Stack>
                    </div>
                    :
                    null
            }

            <CustomModal isShow={showSaveModal} handleClose={handleCloseSaveModal} header={<IconElement iconType={faInfoCircle} headerClass="blue-icon ms-6" color="#0060DF" />} size="md">
                <SaveConfirmationForm />
            </CustomModal>
        </React.Fragment>
    );

}